import { useState, useRef } from "react";
import axios from "axios";
import { Lightbulb, Loader2, Image as ImageIcon, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMG_MB = 3;

export default function SuggestionButton() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const [image, setImage] = useState(null); // { dataUrl, name }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setText(""); setLink(""); setImage(null); setErr(""); setSuccess(false); setLoading(false);
  };

  const onFile = (e) => {
    setErr("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("Bitte ein Bild auswählen."); return; }
    if (f.size > MAX_IMG_MB * 1024 * 1024) {
      setErr(`Bild zu groß (max ${MAX_IMG_MB} MB).`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage({ dataUrl: reader.result, name: f.name });
    reader.onerror = () => setErr("Bild konnte nicht gelesen werden.");
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (text.trim().length < 5) {
      setErr("Bitte beschreibe deinen Vorschlag (min. 5 Zeichen).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/suggestions`,
        {
          text: text.trim(),
          link: link.trim() || null,
          image_data_url: image?.dataUrl || null,
        },
        { withCredentials: true }
      );
      setSuccess(true);
      setTimeout(() => { setOpen(false); reset(); }, 1600);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Fehler beim Senden.");
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="header-suggest-btn"
        title="Verbesserungsvorschlag einreichen — geht direkt an den Admin"
        className="group flex items-center gap-1.5 px-2 md:px-3 h-9 text-[12.5px] font-outfit font-semibold tracking-tight text-[#fbbf24] border border-[#fbbf24]/50 bg-[#fbbf24]/[0.08] hover:bg-[#fbbf24]/20 hover:border-[#fbbf24] hover:shadow-[0_0_20px_-4px_rgba(251,191,36,0.5)] transition-all duration-200"
      >
        <Lightbulb size={14} className="group-hover:fill-[#fbbf24]/40 transition-all" />
        <span className="hidden md:inline">Idee</span>
      </button>

      <Dialog
        open={open}
        onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}
      >
        <DialogContent
          className="bg-transparent border-0 text-white rounded-none max-w-lg p-0 shadow-none max-h-[90vh] overflow-y-auto"
          data-testid="suggestion-dialog"
        >
          <div className="relative">
            <div className="absolute -inset-px pointer-events-none"
              style={{ background: "linear-gradient(135deg, #fbbf2440 0%, transparent 50%, #fbbf2420 100%)" }} />
            <div className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-8">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-px bg-[#fbbf24]/60" />
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#fbbf24]/80 font-outfit font-semibold">
                    Verbesserungsvorschlag
                  </span>
                </div>
                <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">
                  Deine Idee für [KI]NPUT
                </DialogTitle>
                <DialogDescription className="text-[13px] text-white/45 mt-1 leading-relaxed">
                  Text, optional ein Screenshot oder ein Link. Geht direkt an den Admin.
                </DialogDescription>
              </DialogHeader>

              {success ? (
                <div className="mt-8 text-center py-10 border border-[#fbbf24]/30 bg-[#fbbf24]/5">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#fbbf24]/20 flex items-center justify-center">
                    <Lightbulb size={24} className="text-[#fbbf24]" />
                  </div>
                  <p className="text-white font-outfit font-semibold">Danke für deinen Vorschlag!</p>
                  <p className="text-[13px] text-white/50 mt-1">Der Admin wurde benachrichtigt.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4 mt-5">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-outfit font-semibold">
                      Dein Vorschlag *
                    </label>
                    <Textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Was sollten wir verbessern, hinzufügen oder ändern?"
                      maxLength={4000}
                      rows={5}
                      required
                      data-testid="suggestion-text"
                      className="mt-2 bg-white/[0.02] border-white/10 text-white rounded-none focus-visible:ring-[#fbbf24] focus-visible:ring-1 focus-visible:border-[#fbbf24]/60 resize-none"
                    />
                    <p className="text-[11px] text-white/30 mt-1">{text.length} / 4000</p>
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-outfit font-semibold">
                      Link (optional)
                    </label>
                    <Input
                      type="text"
                      value={link}
                      onChange={(e) => setLink(e.target.value)}
                      placeholder="z. B. https://beispiel.de"
                      data-testid="suggestion-link"
                      className="mt-2 bg-white/[0.02] border-white/10 text-white h-11 rounded-none focus-visible:ring-[#fbbf24] focus-visible:ring-1 focus-visible:border-[#fbbf24]/60"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-outfit font-semibold">
                      Screenshot (optional, max {MAX_IMG_MB} MB)
                    </label>
                    {!image ? (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        data-testid="suggestion-image-btn"
                        className="mt-2 w-full h-20 border border-dashed border-white/15 hover:border-[#fbbf24]/60 text-white/50 hover:text-[#fbbf24] transition-colors flex items-center justify-center gap-2 text-[13px] font-outfit"
                      >
                        <ImageIcon size={16} />
                        Bild auswählen oder hier ablegen
                      </button>
                    ) : (
                      <div className="mt-2 relative border border-white/10 bg-white/[0.02] p-2">
                        <img
                          src={image.dataUrl}
                          alt="Vorschau"
                          className="max-h-40 w-auto mx-auto"
                          data-testid="suggestion-image-preview"
                        />
                        <button
                          type="button"
                          onClick={() => { setImage(null); if (fileRef.current) fileRef.current.value = ""; }}
                          data-testid="suggestion-image-remove"
                          className="absolute top-1 right-1 p-1 bg-black/80 hover:bg-red-500/80 transition-colors"
                        >
                          <X size={14} className="text-white" />
                        </button>
                        <p className="text-[11px] text-white/40 mt-1 truncate">{image.name}</p>
                      </div>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onFile}
                    />
                  </div>

                  {err && <p className="text-[13px] text-red-400" data-testid="suggestion-error">{err}</p>}

                  <Button
                    type="submit"
                    disabled={loading || text.trim().length < 5}
                    data-testid="suggestion-submit-btn"
                    className="w-full bg-[#fbbf24] text-black hover:bg-[#fbbf24] hover:shadow-[0_0_32px_-4px_rgba(251,191,36,0.6)] rounded-none font-outfit font-semibold h-11 text-[14px] tracking-wide transition-all disabled:opacity-40"
                  >
                    {loading ? (
                      <><Loader2 size={16} className="animate-spin mr-2" /> Wird gesendet…</>
                    ) : (
                      <>Vorschlag einreichen</>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
